import React, { useEffect, useState } from 'react';
import { ListFilter, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { FilterableSelect } from '../ui/FilterableSelect';
import {
  ADVANCED_FILTER_OPTIONS,
  EMPTY_ADVANCED_FILTER,
  advancedFilterLabel,
  advancedFilterPlaceholder,
  isAdvancedFilterActive,
  normalizeAdvancedFilterValues,
  splitAdvancedFilterValues,
} from '../../features/deliveries/advancedFilter';

/**
 * "Filtro avanzado" of the deliveries listing: one criterion and a pasted list
 * of values, on top of whatever other filters are set.
 *
 * It renders two things into the filter bar it lives in: the toggle (a pill
 * that, once a filter is applied, names the criterion and counts the values)
 * and, while open, a full-width row with the criterion select, the values
 * input and the apply button. The row is pushed to the end of the bar with
 * `order-last`, so opening it never moves the pills already on screen.
 *
 * The draft is local until "Aplicar": the listing asks the server on every
 * filter change, and a list is typed or pasted in pieces. Nothing is sent
 * without a criterion and at least one value.
 *
 * Only mounted when the plan has the `advanced_filter` addon; the backend
 * refuses the parameters otherwise.
 *
 * @param {object} props
 * @param {object} props.filters - The slice's `filters`
 * @param {(patch: object) => void} props.onFilterChange - Receives a patch with
 *   both `advanced_filter_field` and `advanced_filter_values`.
 */
export const AdvancedFilter = ({ filters, onFilterChange }) => {
  const appliedField = filters?.advanced_filter_field || '';
  const appliedValues = filters?.advanced_filter_values || '';
  const active = isAdvancedFilterActive(filters);

  const [open, setOpen] = useState(false);
  const [draftField, setDraftField] = useState(appliedField);
  const [draftValues, setDraftValues] = useState(appliedValues);

  // "Limpiar filtros" (or a stale persisted filter being neutralised) changes
  // the applied filter from outside; the draft follows it.
  useEffect(() => {
    setDraftField(appliedField);
    setDraftValues(appliedValues);
  }, [appliedField, appliedValues]);

  const draftCount = splitAdvancedFilterValues(draftValues).length;
  const canApply = Boolean(draftField) && draftCount > 0;

  const apply = () => {
    if (!canApply) return;
    onFilterChange({
      advanced_filter_field: draftField,
      advanced_filter_values: normalizeAdvancedFilterValues(draftValues),
    });
    setOpen(false);
  };

  const clear = () => {
    onFilterChange({ ...EMPTY_ADVANCED_FILTER });
    setOpen(false);
  };

  const appliedCount = splitAdvancedFilterValues(appliedValues).length;
  const toggleLabel = active
    ? `Filtro avanzado: ${advancedFilterLabel(appliedField)} (${appliedCount})`
    : 'Filtro avanzado';

  return (
    <>
      <div className="flex items-center gap-1" data-testid="advanced-filter">
        <Button
          variant={active ? 'primary' : 'ghost'}
          size="sm"
          icon={ListFilter}
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          data-testid="advanced-filter-toggle"
        >
          {toggleLabel}
        </Button>
        {active && (
          <button
            type="button"
            onClick={clear}
            aria-label="Quitar filtro avanzado"
            title="Quitar filtro avanzado"
            className="p-1 rounded-full text-text-muted hover:text-ninja-dark dark:hover:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div
          data-testid="advanced-filter-panel"
          className="order-last basis-full flex flex-col md:flex-row md:items-center gap-3 bg-white dark:bg-[#1a1a1a] border border-border rounded-xl px-4 py-3"
        >
          <FilterableSelect
            label="Criterio:"
            value={draftField}
            onChange={(value) => setDraftField(value)}
            options={ADVANCED_FILTER_OPTIONS}
            placeholder="Elegir"
            testId="advanced-filter-field"
          />

          {/* A textarea, not an input: an input drops the line breaks of a
              pasted spreadsheet column and glues the values together. Enter
              applies; Shift+Enter breaks the line, which counts as a comma. */}
          <textarea
            rows={1}
            value={draftValues}
            onChange={(event) => setDraftValues(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                apply();
              }
            }}
            placeholder={advancedFilterPlaceholder(draftField)}
            aria-label="Valores separados por coma"
            data-testid="advanced-filter-values"
            className="flex-1 min-w-[220px] resize-none bg-transparent text-sm outline-none border border-border rounded-lg px-3 py-1.5 dark:text-white placeholder:text-text-muted"
          />

          <span className="text-xs text-text-muted whitespace-nowrap" data-testid="advanced-filter-count">
            {draftCount === 1 ? '1 valor' : `${draftCount} valores`}
          </span>

          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={apply} disabled={!canApply} data-testid="advanced-filter-apply">
              Aplicar
            </Button>
            <Button variant="ghost" size="sm" onClick={active ? clear : () => setOpen(false)}>
              {active ? 'Quitar' : 'Cancelar'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
